import axios, { type AxiosInstance, type AxiosRequestConfig } from "axios";
import { axiosApiInstance, axiosStorageInstance } from "../axios";
import type { ApiErrorPayload, ApiPayload, ApiResult } from "../types/api";

type HttpMethod = "get" | "post" | "put" | "patch" | "delete";

function authenticatedConfig(contentType?: string, timezone?: string): AxiosRequestConfig {
  return {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("authToken") ?? ""}`,
      "Content-Type": contentType ?? "application/json",
      ...(timezone ? { "X-Timezone": timezone } : {}),
    },
  };
}

function apiFailure<E>(error: unknown): ApiResult<never, E> {
  const fallback = {
    title: "Error",
    subtitle: "We could not complete the action.",
  } satisfies ApiErrorPayload;

  if (axios.isAxiosError<E>(error)) {
    return {
      success: false,
      data: error.response?.data ?? (fallback as E),
      errorStatus: error.response?.status,
    };
  }

  return { success: false, data: fallback as E };
}

async function request<T, E = ApiErrorPayload, P extends ApiPayload = ApiPayload>(
  client: AxiosInstance,
  method: HttpMethod,
  endpoint: string,
  payload?: P,
  contentType?: string,
  timezone?: string
): Promise<ApiResult<T, E>> {
  try {
    const config = authenticatedConfig(contentType, timezone);
    const response =
      method === "get" || method === "delete"
        ? await client[method]<T>(endpoint, config)
        : await client[method]<T>(endpoint, payload ?? ({} as P), config);

    return { success: true, data: response.data };
  } catch (error: unknown) {
    return apiFailure<E>(error);
  }
}

export function makePostApiCall<
  T = unknown,
  P extends ApiPayload = ApiPayload,
  E = ApiErrorPayload,
>(endpoint: string, payload: P = {} as P, contentType?: string): Promise<ApiResult<T, E>> {
  return request<T, E, P>(axiosApiInstance, "post", endpoint, payload, contentType);
}

export function makePutApiCall<T = unknown, P extends ApiPayload = ApiPayload, E = ApiErrorPayload>(
  endpoint: string,
  payload: P = {} as P,
  contentType?: string
): Promise<ApiResult<T, E>> {
  return request<T, E, P>(axiosApiInstance, "put", endpoint, payload, contentType);
}

export function makePatchApiCall<
  T = unknown,
  P extends ApiPayload = ApiPayload,
  E = ApiErrorPayload,
>(endpoint: string, payload: P = {} as P, contentType?: string): Promise<ApiResult<T, E>> {
  return request<T, E, P>(axiosApiInstance, "patch", endpoint, payload, contentType);
}

export function makeGetApiCall<T = unknown, E = ApiErrorPayload>(
  endpoint: string
): Promise<ApiResult<T, E>> {
  return request<T, E>(axiosApiInstance, "get", endpoint);
}

export function makeDeleteApiCall<T = unknown, E = ApiErrorPayload>(
  endpoint: string
): Promise<ApiResult<T, E>> {
  return request<T, E>(axiosApiInstance, "delete", endpoint);
}

export function makeGetCall<T = unknown, E = ApiErrorPayload>(
  endpoint: string
): Promise<ApiResult<T, E>> {
  return request<T, E>(axiosStorageInstance, "get", endpoint);
}

/** Calendar-aware requests retain the existing authenticated transport. */
export function makeZonedApiCall<T, P extends ApiPayload = ApiPayload>(
  method: HttpMethod,
  endpoint: string,
  payload?: P
): Promise<ApiResult<T>> {
  return request<T, ApiErrorPayload, P>(
    axiosApiInstance,
    method,
    endpoint,
    payload,
    undefined,
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
}
